import { Button } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getDataSourceOptions } from "@/api";
import { Text } from "@/components/Text";
import { AutomatedSyncConsent } from "@/features/build/dataSources/AutomatedSyncConsent";
import { readSchedule } from "@/features/build/dataSources/scheduleSummary";
import { DraftCard } from "../DraftCard";
import { useDraftContext } from "../DraftContext";
import type { EntityDraftCardProps } from "../registry";
import { DataSourceDraftSummary } from "./DataSourceDraftSummary";
import { useDataSourceDraft } from "./useDataSourceDraft";

/** A data source draft: Create runs the source, its content and its schedule as steps; consent comes first. */
export function DataSourceDraftCard({
  draft,
  state,
  entityUuid,
  updatedAt,
  answerable,
}: Readonly<EntityDraftCardProps>) {
  const { t } = useTranslation("buildChat");
  const { linked, readOnly } = useDraftContext();
  const source = useDataSourceDraft(draft, entityUuid);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const withConsent = (action: () => void) => () => (source.needsConsent ? setPending(() => action) : action());

  // A source created from this card (seen after a reload) whose schedule or background sync didn't take.
  const check = state === "created" && Boolean(entityUuid) && source.canSchedule;
  const created = useQuery({
    ...getDataSourceOptions({ path: { collection_uuid: entityUuid ?? "" } }),
    select: (r) => r.data,
    enabled: check,
  });
  const notApplied =
    check &&
    created.data !== undefined &&
    ((Boolean(source.body.syncSchedule) && !readSchedule(created.data)) ||
      (source.body.enableAutomatedSync === true && !created.data.automatedSyncEnabled));
  const fixError = source.fixSchedule.steps.find((s) => s.status === "failed")?.error;

  return (
    <>
      <DraftCard
        title={source.collection.name ?? linked.find((e) => e.uuid === draft.targetUuid)?.name ?? t("draft.untitled")}
        entityLabel={t("draft.entity.data_source")}
        state={state}
        isUpdate={Boolean(draft.targetUuid)}
        doneUuid={source.doneUuid}
        entityUuid={entityUuid}
        updatedAt={updatedAt}
        missingFields={draft.missingFields}
        questions={draft.questions}
        answerable={answerable}
        busy={source.busy}
        steps={source.steps}
        blocked={source.blocked}
        onCreate={withConsent(source.run)}
        onRetry={source.retry}
        onOpenInForm={source.openInForm}
        detailPath={(uuid) => `/build/data-sources/${uuid}`}
        summary={
          <div className="space-y-3">
            <DataSourceDraftSummary
              body={source.body}
              wantsSchedule={source.wantsSchedule}
              canSchedule={source.canSchedule}
            />
            {notApplied && !readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                <Text variant="body-sm" tone="warning">
                  {t("dataSource.notApplied")}
                </Text>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={source.fixSchedule.busy}
                  onClick={withConsent(source.fixSchedule.run)}
                >
                  {t("dataSource.applySchedule")}
                </Button>
                {fixError && (
                  <Text variant="body-sm" tone="error" role="alert">
                    {fixError}
                  </Text>
                )}
              </div>
            )}
          </div>
        }
      />
      <AutomatedSyncConsent
        open={pending !== null}
        busy={false}
        onCancel={() => setPending(null)}
        onAuthorize={() => {
          pending?.();
          setPending(null);
        }}
      />
    </>
  );
}
